import React from "react";
import PopupBodyItem from "../../Helpers/Popup/PopupParts/PopupBodyItem";
import { formatDate, formatDateString } from "../../../JsHelpers/DateFormat";
import trans from "../../../utils";
import { Tag } from "antd";
import VehicleAttachments from "../../Vehicles/VehicleInf/VehicleAttachments";
import useIsMobile from "../../../hooks/useIsMobile";
import { DownloadOutlined } from "@ant-design/icons";

export default function SubmittalsInf({ values = {}, statusColors }) {
  const isMobile = useIsMobile();
  const getValue = (value, defaultValue = "-----") =>
    value ? value : defaultValue;
  const {
    submittalNumber,
    type = "",
    description,
    discipline = "",
    status = "",
    targetReviewDate = "",
    reviewedByQA,
    reviewedByConsultant,
  } = values;
  const formattedSubmittedAt = targetReviewDate
    ? formatDate(targetReviewDate).formattedDate
    : "-----";
  const getUrl = (type) => {
    const lowerCaseType = type.toLowerCase(); // Normalize to lowercase
    if (lowerCaseType === "image/png") {
      return `${process.env.PUBLIC_URL}/assets/PNG.svg`;
    } else if (
      lowerCaseType === "image/jpg" ||
      lowerCaseType === "image/jpeg"
    ) {
      return `${process.env.PUBLIC_URL}/assets/JPG.svg`;
    } else if (lowerCaseType === "application/pdf") {
      return `${process.env.PUBLIC_URL}/assets/PDF.svg`;
    } else {
      return `${process.env.PUBLIC_URL}/assets/Excel.svg`;
    }
  };
  const truncateItem = (text) => {
    const maxLength = 50;
    return text?.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };
  return (
    <div>
      <p className="fs-16 fw-700 brand-700 text-uppercase">
        {trans("Submittal Details")}
      </p>
      <div className="row">
        <div className="col-lg-6">
          <div className="display-border">
            <PopupBodyItem
              title={trans("Submittal Number")}
              item={getValue(submittalNumber)}
            />
            <PopupBodyItem title={trans("Type")} item={trans(getValue(type))} />
            <PopupBodyItem
              title={trans("Discipline")}
              item={trans(getValue(discipline))}
            />
            <PopupBodyItem
              title={trans("Project")}
              item={getValue(values?.project?.name)}
            />
            <PopupBodyItem
              title={trans("Submitted By")}
              item={getValue(values?.submittedBy?.name)}
            />{" "}
            <PopupBodyItem
              title={trans("Reviewed By Consultant")}
              item={getValue(values?.reviewedByConsultant?.name)}
            />
          </div>
        </div>
        <div className="col-lg-6">
          <div className="display-border">
            <PopupBodyItem
              title={trans("Project")}
              item={getValue(values?.project?.name)}
            />
            <PopupBodyItem
              title={trans("Description")}
              item={getValue(description)}
            />

            <PopupBodyItem
              title={trans("Status")}
              item={getValue(
                <Tag color={statusColors[status] || "default"}>
                  {trans(status)}
                </Tag>,
              )}
            />
            <PopupBodyItem
              title={trans("Review Date")}
              item={getValue(formattedSubmittedAt)}
            />
            <PopupBodyItem
              title={trans("Reviewed By QA")}
              item={getValue(values?.reviewedByQA?.name)}
            />
          </div>
        </div>
      </div>
      {values?.attachments?.length > 0 &&
        values?.attachments?.map((i) => (
          <div key={i.id} className="col-12 mt-4">
            {isMobile ? (
              <>
                <div className="d-flex flex-column gap-3">
                  {values?.attachments?.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center border p-3 rounded pointer"
                      onClick={() => window.open(`${file?.url}`, "_blank")}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <img
                          src={getUrl(file?.type)}
                          alt={file?.mimeType || file?.type || "file"}
                          width={28}
                        />
                        <div>
                          {file?.filename ||
                            `${trans("Attachments")} ${index + 1}`}
                          <br />
                          {file?.size && (
                            <div className="text-muted small">
                              {file.size} MB
                            </div>
                          )}
                          {file?.uploadedBy && (
                            <div className="text-muted small">
                              {trans("Uploaded by")}{" "}
                              <span className="fw-800">
                                {file.uploadedBy?.name}
                              </span>{" "}
                              {trans("on")}{" "}
                              <span className="fw-800">
                                {formatDateString(file.uploadedAt)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <DownloadOutlined
                        className="text-secondary pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const link = document.createElement("a");
                          link.href = `${file?.url}`;
                          link.download =
                            file?.originalName ||
                            file?.name ||
                            file?.filename ||
                            `attachment-${index + 1}`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="attachments p-3 rounded-2xl shadow-md bg-white hover:shadow-lg transition-shadow duration-300 flex items-center justify-between flex-between">
                {/* Left side: File info */}
                <div className="flex items-center gap-3">
                  <img
                    src={`${getUrl(i.type)}`}
                    width={40}
                    height={40}
                    alt="format"
                    className="rounded-md"
                  />
                  <div className="flex flex-col">
                    <a
                      href={i.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-gray-800 hover:text-blue-600 truncate max-w-[400px]"
                      title={i.name}
                    >
                      {truncateItem(i.name)}
                    </a>
                    <div className="text-xs text-gray-500 mt-1">
                      {i.size} MB
                    </div>
                  </div>
                </div>

                {/* Right side: Uploaded info & status */}
                <div className="flex flex-col items-end gap-1">
                  <div className="text-xs text-gray-400">
                    {i?.uploadedBy?.name || "Unknown"}
                  </div>
                  <div className="text-xs text-gray-400">
                    {i?.uploadedAt
                      ? new Date(i.uploadedAt).toLocaleString()
                      : ""}
                  </div>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/Correct.svg`}
                    alt="status"
                    className="w-5 h-5 mt-1"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}
